<template>
    <div class="phone-verification-container">
        <div class="divider"></div>

        <div class="verification-container">
            <div class="verification-header">
                <p class="verification-title">Verification Code</p>
            </div>
            <div class="otp-container">
                <!-- input otp -->
                <prime-input-otp v-model="otp" :length="6" size="xs" @keydown.enter="onEnter" @paste="onPaste">
                    <template #default="{ attrs, events, index }">
                        <input type="text" v-bind="attrs" v-on="events" class="custom-otp-input" />
                        <span v-if="index === 3" class="otp-dash">-</span>
                    </template>
                </prime-input-otp>
            </div>
            <div class="resend-container">
                <p class="resend-text">
                    Didn't get a code?
                    <span
                        :class="{
                            'resend-link': true,
                            'resend-link-active': !isSmsResent && !isResending,
                            'resend-link-disabled': isSmsResent || isResending,
                        }"
                        @click="handleResend"
                    >
                        Click to resend</span
                    >.
                </p>
            </div>
            <div class="divider my-3" v-if="isSmsResent || isInvalidCode"></div>
            <div class="status-container" v-if="isSmsResent">
                <CheckCircleIcon class="status-icon success" />
                <p class="status-text success">Verification code re-sent!</p>
            </div>
            <div class="status-container" v-if="isInvalidCode">
                <ExclamationCircleIcon class="status-icon error" />
                <p class="status-text error">Invalid verification code</p>
            </div>
        </div>
    </div>

    <div class="footer-container">
        <div class="button-container">
            <PrimeButton label="Cancel" @click="closeDialog" variant="secondary" class="p-btn-root_secondary"/>

            <PrimeButton @click="verifyPhone" :loading="isVerifyingOtp" :disabled="otp.length < 6">
                     {{ isVerifyingOtp ? 'Verifying...' : 'Verify & Add' }}
            </PrimeButton>
        </div>
    </div>
</template>
<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/solid';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';

const { handleClearToast } = useGlobalToast();
const isSmsResent = ref(false);
const isResending = ref(false);
const isInvalidCode = ref(false);
const otp = ref('');

//emit
const emit = defineEmits(['closeDialog']);

//inject dialogRef
const dialogRef =   getDialogRef(ModalNames.PHONE_VERIFICATION);

//close dialog
const closeDialog = () => {
    emit('closeDialog');
};

const personStore = usePersonStore();
const vaultStore = useVaultStore();
const isVerifyingOtp = ref(false);

//verify phone
const verifyPhone = async () => {
    try {
        handleClearToast();
        isInvalidCode.value = false;
        isVerifyingOtp.value = true;
        await personStore.verifyPhoneOtp({
            country_code: dialogRef.value.data.country_code.replace('+', ''),
            phone_number: dialogRef.value.data.phone_number,
            code: otp.value,
        });
        
        // If this came from an exposure, suppress the original exposure
        if (dialogRef.value.data.fromExposure && dialogRef.value.data.exposureData) {
            const exposureData = dialogRef.value.data.exposureData;
            const exposureType = exposureData.type || 'phone';
            await vaultStore.updateExposure(true, exposureType, exposureData.value);
            await vaultStore.updatePotentialExposures(exposureType, exposureData.value, true);
            
            // Refresh the potential exposures to update the UI
            await vaultStore.fetchPotentialExposures();
        } else if (dialogRef.value.data.fromExposure) {
            // Fallback for existing logic if no exposure data
            await vaultStore.updateExposure(true, 'phone', dialogRef.value.data.phone_number);
            await vaultStore.updatePotentialExposures('phone', dialogRef.value.data.phone_number, true);
        }
        
        isVerifyingOtp.value = false;
        closeDialog();
    } catch (error) {
        isVerifyingOtp.value = false;
        isInvalidCode.value = true;
        return;
    }
};

const onEnter = (e: KeyboardEvent) => {
    if (!isVerifyingOtp.value) {
        verifyPhone();
    }
};

const onPaste = (event: ClipboardEvent) => {
    setTimeout(() => {
        verifyPhone();
    }, 0);
};

const dialogRefAddPhone = getDialogRef(ModalNames.ADD_PHONE);

const handleResend = async () => {
    if (isSmsResent.value || isResending.value) return;

    isResending.value = true;
    handleClearToast();
    otp.value = '';
    isInvalidCode.value = false;

    const selectedMethodValue = dialogRefAddPhone.value.data.method === 'sms' ? true : false;

    const requestData = {
        phone_number: dialogRefAddPhone?.value.data.phone_number as string,
        country_code: dialogRefAddPhone?.value.data.country_code as string,
        method: dialogRefAddPhone?.value.data.method || 'sms',
        fromExposure: dialogRefAddPhone?.value.data?.fromExposure || false, // Pass through the fromExposure flag
        exposureData: dialogRefAddPhone?.value.data?.exposureData || false, // Pass through the exposure data for suppression
    };

    try {
        await personStore.sendPhoneVerificationCode(
            selectedMethodValue,
            requestData,
            true
        );
        isSmsResent.value = true;

        setTimeout(() => {
            isSmsResent.value = false;
        }, 30000);
    } finally {
        isResending.value = false;
    }
};
</script>
<style lang="scss" scoped>
.phone-verification-container {
    @apply flex flex-col items-start gap-1.5 pt-4 self-stretch;
}

.divider {
    @apply h-px self-stretch bg-zinc-300;
}

.resend-container {
    @apply flex items-start;
}

.resend-text {
    @apply text-zinc-500 font-inter text-sm font-normal leading-5;
}

.resend-link {
    @apply underline cursor-pointer;
}

.resend-link-active {
    @apply text-blue-600;
}

.resend-link-disabled {
    @apply text-gray-600;
}

.verification-container {
    @apply w-full;
}

.verification-header {
    @apply flex flex-col items-start gap-1.5 self-stretch pt-3;
}

.verification-title {
    @apply text-gray-700 text-sm font-medium leading-5 font-inter;
}

.otp-container {
    @apply flex items-start sm:gap-2 gap-px h-16 py-1.5;
}

.status-container {
    @apply flex items-center gap-2 self-stretch;
}

.status-icon {
    @apply w-5 h-5;
}

.status-icon.success {
    @apply text-green-500;
}

.status-icon.error {
    @apply text-red-500;
}

.status-text {
    @apply text-sm font-normal leading-5 font-inter;
}

.status-text.success {
    @apply text-gray-600;
}

.status-text.error {
    @apply text-red-500;
}

//footer
.footer-container {
    @apply flex flex-col justify-center items-end self-stretch pt-8;
}

.button-container {
    @apply flex justify-end items-center gap-3 flex-1 pb-6;
}
</style>
